/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component, cloneElement, Fragment } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keysOf } from '../common';
import { EuiPortal } from '../portal';
import { EuiToolTipPopover } from './tool_tip_popover';
import { findPopoverPosition, htmlIdGenerator } from '../../services';
import { EuiResizeObserver } from '../observer/resize_observer';
var positionsToClassNameMap = {
  top: 'euiToolTip--top',
  right: 'euiToolTip--right',
  bottom: 'euiToolTip--bottom',
  left: 'euiToolTip--left'
};
export var POSITIONS = keysOf(positionsToClassNameMap);
var delayToClassNameMap = {
  regular: null,
  long: 'euiToolTip--delayLong'
};
export var DELAY = keysOf(delayToClassNameMap);
var DEFAULT_TOOLTIP_STYLES = {
  // position the tooltip content near the top-left
  // corner of the window so it can't create scrollbars
  // 50,50 because who knows what negative margins, padding, etc
  top: 50,
  left: 50,
  // just in case, avoid any potential flicker by hiding
  // the tooltip before it is positioned
  opacity: 0
};
export class EuiToolTip extends Component {
  _isMounted = false;
  anchor = null;
  popover = null;
  state = {
    visible: false,
    hasFocus: false,
    calculatedPosition: this.props.position,
    toolTipStyles: DEFAULT_TOOLTIP_STYLES,
    arrowStyles: undefined,
    id: this.props.id || htmlIdGenerator()()
  };
  static defaultProps = {
    position: 'top',
    delay: 'regular'
  };

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.visible === false && this.state.visible === true) {
      requestAnimationFrame(this.testAnchor);
    }
  }

  testAnchor = () => {
    // when the tooltip is visible, this checks if the anchor is still part of document
    // this fixes when the react root is removed from the dom without unmounting
    // https://github.com/elastic/eui/issues/1105
    if (document.body.contains(this.anchor) === false) {
      // the anchor is no longer part of `document`
      this.hideToolTip();
    } else {
      if (this.state.visible) {
        // if still visible, keep checking
        requestAnimationFrame(this.testAnchor);
      }
    }
  };
  setPopoverRef = ref => {
    this.popover = ref; // if the popover has been unmounted, clear
    // any previous knowledge about its size

    if (ref == null) {
      this.setState({
        toolTipStyles: DEFAULT_TOOLTIP_STYLES,
        arrowStyles: undefined
      });
    }
  };
  showToolTip = () => {
    this.setState({
      visible: true
    });
  };
  positionToolTip = () => {
    const requestedPosition = this.props.position;

    if (!this.anchor || !this.popover) {
      return;
    }

    const {
      position,
      left,
      top,
      arrow
    } = findPopoverPosition({
      anchor: this.anchor,
      popover: this.popover,
      position: requestedPosition,
      offset: 16,
      // offset popover 16px from the anchor
      arrowConfig: {
        arrowWidth: 12,
        arrowBuffer: 4
      }
    }); // If encroaching the right edge of the window:
    // When `props.content` changes and is longer than `prevProps.content`, the tooltip width remains and
    // the resizeObserver callback will fire twice (once for vertical resize caused by text line wrapping,
    // once for a subsequent position correction) and cause a flash rerender and reposition.
    // To prevent this, we can orient from the right so that text line wrapping does not occur, negating
    // the second resizeObserver callback call.

    const windowWidth = document.documentElement.clientWidth || window.innerWidth;
    const useRightValue = windowWidth / 2 < left;
    const toolTipStyles = {
      top,
      left: useRightValue ? 'auto' : left,
      right: useRightValue ? windowWidth - left - this.popover.offsetWidth : 'auto'
    };
    this.setState({
      visible: true,
      calculatedPosition: position,
      toolTipStyles,
      arrowStyles: arrow
    });
  };
  hideToolTip = () => {
    if (this._isMounted) {
      this.setState({
        visible: false
      });
    }
  };
  onFocus = () => {
    this.setState({
      hasFocus: true
    });
    this.showToolTip();
  };
  onBlur = () => {
    this.setState({
      hasFocus: false
    });
    this.hideToolTip();
  };
  onMouseOut = e => {
    // Prevent mousing over children from hiding the tooltip by testing for whether the mouse has
    // left the anchor for a non-child.
    if (this.anchor === e.relatedTarget || this.anchor != null && !this.anchor.contains(e.relatedTarget)) {
      if (!this.state.hasFocus) {
        this.hideToolTip();
      }
    }

    if (this.props.onMouseOut) {
      this.props.onMouseOut(e);
    }
  };

  render() {
    const {
      children,
      className,
      anchorClassName,
      content,
      title,
      delay,
      ...rest
    } = this.props;
    const {
      arrowStyles,
      id,
      toolTipStyles,
      visible
    } = this.state;
    const classes = classNames('euiToolTip', positionsToClassNameMap[this.state.calculatedPosition], delayToClassNameMap[delay], className);
    const anchorClasses = classNames('euiToolTipAnchor', anchorClassName);
    let tooltip;

    if (visible && (content || title)) {
      tooltip = <EuiPortal>
          <EuiToolTipPopover className={classes} style={toolTipStyles} positionToolTip={this.positionToolTip} popoverRef={this.setPopoverRef} title={title} id={id} role="tooltip" {...rest}>
            <div style={arrowStyles} className="euiToolTip__arrow" />
            <EuiResizeObserver onResize={this.positionToolTip}>
              {resizeRef => <div ref={resizeRef}>{content}</div>}
            </EuiResizeObserver>
          </EuiToolTipPopover>
        </EuiPortal>;
    }

    const anchor = // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
    <span ref={anchor => this.anchor = anchor} className={anchorClasses} onMouseOver={this.showToolTip} onMouseOut={this.onMouseOut}>
        {
        /**
        * Re: jsx-a11y/mouse-events-have-key-events
        * We apply onFocus, onBlur, etc to the children element because that's the element
        * the user will be interacting with, as opposed to the enclosing anchor element.
        * For example, if the inner component is a button and the user tabs to it, we want
        * the enter key to trigger the button. That won't work if the enclosing anchor
        * element has focus.
        */
      }
        {cloneElement(children, {
        onFocus: this.showToolTip,
        onBlur: this.hideToolTip,
        ...(visible && {
          'aria-describedby': this.state.id
        })
      })}
      </span>;
    return <Fragment>
        {anchor}
        {tooltip}
      </Fragment>;
  }

}
EuiToolTip.propTypes = {
  /**
     * Passes onto the the trigger.
     */
  anchorClassName: PropTypes.string,

  /**
     * The in-view trigger for your tooltip.
     */
  children: PropTypes.element.isRequired,

  /**
     * Passes onto the tooltip itself, not the trigger.
     */
  className: PropTypes.string,

  /**
     * The main content of your tooltip.
     */
  content: PropTypes.node,

  /**
     * Delay before showing tooltip. Good for repeatable items.
     */
  delay: PropTypes.oneOf(["regular", "long"]).isRequired,

  /**
     * An optional title for your tooltip.
     */
  title: PropTypes.node,

  /**
     * Unless you provide one, this will be randomly generated.
     */
  id: PropTypes.string,

  /**
     * Suggested position. If there is not enough room for it this will be changed.
     */
  position: PropTypes.oneOf(["top", "right", "bottom", "left"]).isRequired,

  /**
     * If supplied, called when mouse movement causes the tool tip to be
     * hidden.
     */
  onMouseOut: PropTypes.func
};

try {
  // @ts-ignore
  EuiToolTip.displayName = "EuiToolTip"; // @ts-ignore

  EuiToolTip.__docgenInfo = {
    "description": "",
    "displayName": "EuiToolTip",
    "props": {
      "anchorClassName": {
        "defaultValue": null,
        "description": "Passes onto the the trigger.",
        "name": "anchorClassName",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "children": {
        "defaultValue": null,
        "description": "The in-view trigger for your tooltip.",
        "name": "children",
        "required": false,
        "type": {
          "name": "string | number | boolean | {} | ReactElement<any, string | ((props: any) => ReactElement<any, string | ... | (new (props: any) => Component<any, any, any>)> | null) | (new (props: any) => Component<...>)> | ... 4 more ... | undefined"
        }
      },
      "className": {
        "defaultValue": null,
        "description": "Passes onto the tooltip itself, not the trigger.",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "content": {
        "defaultValue": null,
        "description": "The main content of your tooltip.",
        "name": "content",
        "required": false,
        "type": {
          "name": "ReactNode"
        }
      },
      "delay": {
        "defaultValue": {
          value: "regular"
        },
        "description": "Delay before showing tooltip. Good for repeatable items.",
        "name": "delay",
        "required": false,
        "type": {
          "name": "enum",
          "value": [{
            "value": "\"regular\""
          }, {
            "value": "\"long\""
          }]
        }
      },
      "title": {
        "defaultValue": null,
        "description": "An optional title for your tooltip.",
        "name": "title",
        "required": false,
        "type": {
          "name": "ReactNode"
        }
      },
      "id": {
        "defaultValue": null,
        "description": "Unless you provide one, this will be randomly generated.",
        "name": "id",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "position": {
        "defaultValue": {
          value: "top"
        },
        "description": "Suggested position. If there is not enough room for it this will be changed.",
        "name": "position",
        "required": false,
        "type": {
          "name": "enum",
          "value": [{
            "value": "\"left\""
          }, {
            "value": "\"right\""
          }, {
            "value": "\"top\""
          }, {
            "value": "\"bottom\""
          }]
        }
      },
      "onMouseOut": {
        "defaultValue": null,
        "description": "If supplied, called when mouse movement causes the tool tip to be\nhidden.",
        "name": "onMouseOut",
        "required": false,
        "type": {
          "name": "((event: MouseEvent<HTMLSpanElement, MouseEvent>) => void) | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/tool_tip/tool_tip.tsx#EuiToolTip"] = {
      docgenInfo: EuiToolTip.__docgenInfo,
      name: "EuiToolTip",
      path: "src/components/tool_tip/tool_tip.tsx#EuiToolTip"
    };
} catch (__react_docgen_typescript_loader_error) {}